import { useCallback, useEffect, useState } from "react";

interface UseMediaDevicesReturn {
  devices: MediaDeviceInfo[];
  selectedDeviceId: string | undefined;
  setSelectedDeviceId: (id: string) => void;
}

export const useMediaDevices = (): UseMediaDevicesReturn => {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<
    string | undefined
  >();

  const refreshDevices = useCallback(async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = allDevices.filter(
        (device) => device.kind === "audioinput",
      );
      setDevices(audioInputs);

      setSelectedDeviceId((current) => {
        const currentStillAvailable = audioInputs.some(
          (d) => d.deviceId === current,
        );
        if (current && currentStillAvailable) {
          return current;
        }
        return audioInputs[0]?.deviceId;
      });
    } catch {
      setDevices([]);
      setSelectedDeviceId(undefined);
    }
  }, []);

  useEffect(() => {
    refreshDevices();

    navigator.mediaDevices?.addEventListener("devicechange", refreshDevices);
    return () => {
      navigator.mediaDevices?.removeEventListener(
        "devicechange",
        refreshDevices,
      );
    };
  }, [refreshDevices]);

  return { devices, selectedDeviceId, setSelectedDeviceId };
};
